import React from "react";
import { Alert, Button, Card, Form, Input, Space, Switch, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getUserProfile, patchUserProfile, type UserProfile } from "../api";
import i18n, { resolveAppLanguage } from "../i18n";
import { persistLocaleToProfile } from "../auth/profileLocale";

export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await getUserProfile();
      setProfile(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("compliance.notificationPrefs.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async (patch: Partial<UserProfile>) => {
    if (!profile) return;
    setSaving(true);
    setError(null);
    try {
      const next = await patchUserProfile({
        email_enabled: patch.email_enabled ?? profile.email_enabled,
        telegram_enabled: patch.telegram_enabled ?? profile.telegram_enabled,
        bell_enabled: patch.bell_enabled ?? profile.bell_enabled,
        telegram_chat_id:
          patch.telegram_chat_id !== undefined ? patch.telegram_chat_id : profile.telegram_chat_id,
        locale: patch.locale ?? profile.locale,
      });
      setProfile(next);
      if (patch.locale && patch.locale !== resolveAppLanguage(i18n.language)) {
        await i18n.changeLanguage(resolveAppLanguage(patch.locale));
      }
      message.success(t("compliance.notificationPrefs.saved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("compliance.notificationPrefs.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="notification-settings-page">
      <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 560 }}>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            {t("compliance.notificationPrefs.title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t("compliance.notificationPrefs.subtitle")}
          </Typography.Paragraph>
          <Link to="/dds">{t("compliance.notificationPrefs.backToCases")}</Link>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        <Card loading={loading}>
          {profile && (
            <Form layout="vertical">
              <Form.Item label={t("compliance.notificationPrefs.email")}>
                <Switch
                  checked={profile.email_enabled}
                  data-testid="pref-email-enabled"
                  onChange={(checked) => void save({ email_enabled: checked })}
                  disabled={saving}
                />
              </Form.Item>
              <Form.Item label={t("compliance.notificationPrefs.bell")}>
                <Switch
                  checked={profile.bell_enabled}
                  data-testid="pref-bell-enabled"
                  onChange={(checked) => void save({ bell_enabled: checked })}
                  disabled={saving}
                />
              </Form.Item>
              <Form.Item label={t("compliance.notificationPrefs.telegram")}>
                <Switch
                  checked={profile.telegram_enabled}
                  data-testid="pref-telegram-enabled"
                  onChange={(checked) => void save({ telegram_enabled: checked })}
                  disabled={saving}
                />
              </Form.Item>
              <Form.Item
                label={t("compliance.notificationPrefs.telegramChatId")}
                extra={t("compliance.notificationPrefs.telegramChatIdHelp")}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    data-testid="pref-telegram-chat-id"
                    defaultValue={profile.telegram_chat_id || ""}
                    key={profile.telegram_chat_id || "empty"}
                    placeholder="123456789"
                    onBlur={(e) => {
                      const value = e.target.value.trim() || null;
                      if (value !== (profile.telegram_chat_id || null)) {
                        void save({
                          telegram_chat_id: value,
                          telegram_enabled: value ? true : profile.telegram_enabled,
                        });
                      }
                    }}
                  />
                  <Button
                    type="primary"
                    loading={saving}
                    data-testid="pref-telegram-save"
                    onClick={() => {
                      const el = document.querySelector(
                        '[data-testid="pref-telegram-chat-id"]'
                      ) as HTMLInputElement | null;
                      const value = (el?.value || "").trim() || null;
                      void save({
                        telegram_chat_id: value,
                        telegram_enabled: value ? true : profile.telegram_enabled,
                      });
                    }}
                  >
                    {t("compliance.notificationPrefs.saveChatId")}
                  </Button>
                </Space.Compact>
              </Form.Item>
              <Form.Item label={t("compliance.notificationPrefs.locale")}>
                <Typography.Text code>{profile.locale}</Typography.Text>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
                  {t("compliance.notificationPrefs.localeHelp")}
                </Typography.Paragraph>
                <Button
                  data-testid="pref-sync-locale"
                  onClick={() => {
                    const lng = resolveAppLanguage(i18n.language);
                    void (async () => {
                      await persistLocaleToProfile(lng);
                      await save({ locale: lng });
                    })();
                  }}
                >
                  {t("compliance.notificationPrefs.syncLocale")}
                </Button>
              </Form.Item>
            </Form>
          )}
        </Card>
      </Space>
    </div>
  );
}
